import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import PageHeader from "@/components/feature/PageHeader";
import { useTheme } from "@/context/ThemeContext";

export default function AboutPage() {
  const { t } = useTranslation("home");
  const { effectiveTheme } = useTheme();
  const isLight = effectiveTheme === "light";

  const values = [
    { icon: "ri-shield-check-line", titleKey: "about.value1_title", descKey: "about.value1_desc" },
    { icon: "ri-compass-3-line", titleKey: "about.value2_title", descKey: "about.value2_desc" },
    { icon: "ri-lightbulb-line", titleKey: "about.value3_title", descKey: "about.value3_desc" },
    { icon: "ri-eye-line", titleKey: "about.value4_title", descKey: "about.value4_desc" },
  ];

  return (
    <div
      className={`min-h-screen flex flex-col ${
        isLight ? "bg-[#f5f5f7]" : "bg-cosmic-950"
      }`}
    >
      <PageHeader title={t("about.title")} subtitle={t("about.subtitle")} />

      <main className="flex-1 mx-auto w-full max-w-3xl px-6 sm:px-10 py-10 sm:py-16">
        {/* Brand Intro */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-12"
        >
          <div className="flex items-center gap-3 mb-6">
            <img
              src={isLight
                ? "/images/home/5ca4f114-ceca-45cb-82ea-08e6275d96a2.png"
                : "/images/home/adaptive-icon-foreground.png"
              }
              alt={t("about.brand")}
              className="w-14 h-14 object-contain"
            />
            <div>
              <h2
                className={`text-xl font-bold ${
                  isLight ? "text-[#1c1c1e]" : "text-white"
                }`}
              >
                {t("about.brand")}
              </h2>
              <p
                className={`text-sm ${
                  isLight ? "text-[#6e6e73]" : "text-silver-500"
                }`}
              >
                {t("about.brand_slogan")}
              </p>
            </div>
          </div>
          <p
            className={`text-sm leading-7 ${
              isLight ? "text-[#6e6e73]" : "text-silver-400"
            }`}
          >
            {t("about.intro")}
          </p>
        </motion.div>

        {/* Mission */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="mb-12"
        >
          <div className="flex items-center gap-2 mb-4">
            <div
              className={`w-1 h-5 rounded-full ${
                isLight ? "bg-[#4a4a4c]" : "bg-gold-500"
              }`}
            />
            <h3
              className={`text-base font-semibold ${
                isLight ? "text-[#1c1c1e]" : "text-white"
              }`}
            >
              {t("about.mission_title")}
            </h3>
          </div>
          <div
            className={`rounded-2xl border p-6 ${
              isLight
                ? "border-black/10 bg-white/70"
                : "border-white/10 bg-cosmic-900/40"
            }`}
          >
            <p
              className={`text-sm leading-7 mb-4 ${
                isLight ? "text-[#6e6e73]" : "text-silver-400"
              }`}
            >
              {t("about.mission_p1")}
            </p>
            <p
              className={`text-sm leading-7 mb-4 ${
                isLight ? "text-[#6e6e73]" : "text-silver-400"
              }`}
            >
              {t("about.mission_p2")}
            </p>
            <p
              className={`text-sm leading-7 ${
                isLight ? "text-[#6e6e73]" : "text-silver-400"
              }`}
            >
              {t("about.mission_p3")}
            </p>
          </div>
        </motion.div>

        {/* Core Values */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="mb-12"
        >
          <div className="flex items-center gap-2 mb-4">
            <div
              className={`w-1 h-5 rounded-full ${
                isLight ? "bg-[#4a4a4c]" : "bg-gold-500"
              }`}
            />
            <h3
              className={`text-base font-semibold ${
                isLight ? "text-[#1c1c1e]" : "text-white"
              }`}
            >
              {t("about.values_title")}
            </h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {values.map((item, index) => (
              <motion.div
                key={item.titleKey}
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.25 + index * 0.08 }}
                className={`rounded-2xl border p-5 ${
                  isLight
                    ? "border-black/10 bg-white/70"
                    : "border-white/10 bg-cosmic-900/40"
                }`}
              >
                <div
                  className={`w-9 h-9 rounded-lg border flex items-center justify-center mb-3 ${
                    isLight
                      ? "bg-[#4a4a4c]/10 border-[#2d2d2d]/20"
                      : "bg-gold-500/10 border-gold-500/20"
                  }`}
                >
                  <i
                    className={`${item.icon} text-lg ${
                      isLight ? "text-[#2d2d2d]" : "text-gold-500"
                    }`}
                  />
                </div>
                <h4
                  className={`text-sm font-semibold mb-2 ${
                    isLight ? "text-[#1c1c1e]" : "text-white"
                  }`}
                >
                  {t(item.titleKey)}
                </h4>
                <p
                  className={`text-xs leading-6 ${
                    isLight ? "text-[#6e6e73]" : "text-silver-500"
                  }`}
                >
                  {t(item.descKey)}
                </p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </main>
    </div>
  );
}